'use client'

import Button from '@/components/ui/Button'
import type { Tables } from '@/types/types_db'
import { checkoutWithStripe } from '@/utils/saas/stripe/server'
import { getErrorRedirect } from '@/utils/saas/helpers'
import { User } from '@supabase/supabase-js'
import cn from 'classnames'
import { useRouter, usePathname } from 'next/navigation'
import { useState } from 'react'

type Subscription = Tables<'subscriptions'>
type Product = Tables<'products'>
type Price = Tables<'prices'>
interface ProductWithPrices extends Product {
  prices: Price[]
}
interface PriceWithProduct extends Price {
  products: Product | null
}
interface SubscriptionWithProduct extends Subscription {
  prices: PriceWithProduct | null
}

interface Props {
  user: User | null | undefined
  products: ProductWithPrices[]
  subscription: SubscriptionWithProduct | null
}

type BillingInterval = 'lifetime' | 'year' | 'month'

export default function Pricing({
  user,
  products,
  subscription,
}: Readonly<Props>) {
  const intervals = Array.from(
    new Set(
      products.flatMap((product) =>
        product?.prices?.map((price) => price?.interval),
      ),
    ),
  )
  const router = useRouter()
  const [billingInterval, setBillingInterval] =
    useState<BillingInterval>('month')
  const [priceIdLoading, setPriceIdLoading] = useState<string>()
  const currentPath = usePathname() ?? '/' // @@BUG: Hack because path must be defined.

  const handleStripeCheckout = async (price: Price) => {
    setPriceIdLoading(price.id)

    if (!user) {
      setPriceIdLoading(undefined)
      // @@TODO
      return router.push('/saas/signin/signup')
    }

    const { errorRedirect, url, sessionId } = await checkoutWithStripe(
      price,
      currentPath,
    )

    if (errorRedirect) {
      setPriceIdLoading(undefined)
      return router.push(errorRedirect)
    }

    if (!url) {
      setPriceIdLoading(undefined)
      return router.push(
        getErrorRedirect(
          currentPath,
          'An unknown error occurred.',
          'Please try again later or contact a system administrator.',
        ),
      )
    }

    // NOTE: Changed from redirectToCheckout() to direct URL redirect for Stripe Clover (2025-09-30)
    // @@TODO: Do not use window.location.href, but window.location.assign() instead, to prevent adding the checkout URL to the browser history, which would allow users to navigate back to it and potentially cause issues with expired sessions.
    //window.location.href = sessionId // NOSONAR
    //window.location.assign(sessionId) // NOSONAR
    window.location.assign(url) // NOSONAR

    setPriceIdLoading(undefined)
  }

  if (!products.length) {
    return (
      <section>
        <div className="max-w-6xl px-4 py-8 mx-auto sm:py-24 sm:px-6 lg:px-8">
          <div className="sm:flex sm:flex-col sm:align-center"></div>
          <p className="text-4xl font-extrabold text-theme-black dark:text-theme-white sm:text-center sm:text-6xl">
            {'No subscription pricing plans found. Create them in your '}
            <a
              className="text-pink-500 underline"
              href="https://dashboard.stripe.com/products"
              rel="noopener noreferrer"
              target="_blank"
            >
              {'Stripe Dashboard'}
            </a>
          </p>
        </div>
      </section>
    )
  } else {
    return (
      <section>
        <div className="max-w-6xl px-4 py-8 mx-auto sm:py-24 sm:px-6 lg:px-8">
          <div className="sm:flex sm:flex-col sm:align-center">
            <h1 className="text-4xl font-extrabold sm:text-center sm:text-6xl">
              Pricing Plans
            </h1>
            <p className="max-w-2xl m-auto mt-5 text-xl text-theme-gray-800 dark:text-theme-gray-200 sm:text-center sm:text-2xl">
              Start building for free, then add a site plan to go live. Account
              plans unlock additional features.
            </p>
            <div className="relative self-center mt-6 bg-theme-gray-100 dark:bg-theme-gray-900 rounded-lg p-0.5 flex sm:mt-8 border border-theme-gray-200 dark:border-theme-gray-800">
              {intervals.includes('month') && (
                <button
                  onClick={() => setBillingInterval('month')}
                  type="button"
                  className={`${
                    billingInterval === 'month'
                      ? 'relative w-1/2 bg-theme-gray-300 dark:bg-theme-gray-700 border-theme-gray-800 dark:border-theme-gray-200 shadow-xs text-theme-black dark:text-theme-white'
                      : 'ml-0.5 relative w-1/2 border border-transparent text-theme-gray-600 dark:text-theme-gray-400'
                  } rounded-md m-1 py-2 text-sm font-medium whitespace-nowrap focus-custom sm:w-auto sm:px-8`}
                >
                  Monthly billing
                </button>
              )}
              {intervals.includes('year') && (
                <button
                  onClick={() => setBillingInterval('year')}
                  type="button"
                  className={`${
                    billingInterval === 'year'
                      ? 'relative w-1/2 bg-theme-gray-300 dark:bg-theme-gray-700 border-theme-gray-200 dark:border-theme-gray-800 shadow-xs text-theme-black dark:text-theme-white'
                      : 'ml-0.5 relative w-1/2 border border-transparent text-theme-gray-600 dark:text-theme-gray-400'
                  } rounded-md m-1 py-2 text-sm font-medium whitespace-nowrap focus-custom sm:w-auto sm:px-8`}
                >
                  Yearly billing
                </button>
              )}
            </div>
          </div>
          <div
            id="pricing"
            className="mt-12 space-y-0 sm:mt-16 flex flex-wrap justify-center gap-6 lg:max-w-4xl lg:mx-auto xl:max-w-none xl:mx-0"
          >
            {products.map((product) => {
              const price = product?.prices?.find(
                (price) => price.interval === billingInterval,
              )
              if (!price) return null
              const priceString = new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: price.currency!,
                minimumFractionDigits: 0,
              }).format((price?.unit_amount ?? 0) / 100)
              return (
                <div
                  key={product.id}
                  className={cn(
                    'flex flex-col rounded-lg shadow-xs divide-y divide-theme-gray-400 dark:divide-theme-gray-600 bg-theme-gray-100 dark:bg-theme-gray-900',
                    {
                      'border border-pink-500': subscription
                        ? product.name === subscription?.prices?.products?.name
                        : product.name === 'Freelancer',
                    },
                    'flex-1', // This makes the flex item grow to fill the space
                    'basis-1/3', // Assuming you want each card to take up roughly a third of the container's width
                    'max-w-xs', // Sets a maximum width to the cards to prevent them from getting too large
                  )}
                >
                  <div className="p-6">
                    <h2 className="text-2xl font-semibold leading-6">
                      {product.name}
                    </h2>
                    <p className="mt-4 text-theme-gray-700 dark:text-theme-gray-300">
                      {product.description}
                    </p>
                    <p className="mt-8">
                      <span className="text-5xl font-extrabold">
                        {priceString}
                      </span>
                      <span className="text-base font-medium text-theme-gray-900 dark:text-theme-gray-100">
                        /{billingInterval}
                      </span>
                    </p>
                    <Button
                      variant="slim"
                      type="button"
                      loading={priceIdLoading === price.id}
                      onClick={() => {
                        // NOTE: Intentionally not awaited.
                        void handleStripeCheckout(price)
                      }}
                      className="block w-full py-2 mt-8 text-sm font-semibold text-center rounded-md"
                    >
                      {subscription ? 'Manage' : 'Subscribe'}
                    </Button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>
    )
  }
}
